import * as React from 'react'

import { Skeleton } from '../../../../components/ui/skeleton'
import { encodeQrMatrix, qrMatrixPath, type QrMatrix } from './qr-matrix'

/** Dark modules on an always-white tile so the code scans in both themes. */
const DARK_MODULE = '#0B0D0F'
const LIGHT_TILE = '#FFFFFF'
const QUIET_ZONE_MODULES = 4

interface QrCodeSvgProps {
  value: string
  /** Target edge length in CSS pixels; snapped to a whole pixel count per module. */
  size?: number
  /** Accessible name for the image; never the encoded value (it holds a secret). */
  label: string
}

type EncodeState =
  | { kind: 'loading' }
  | { kind: 'ready'; value: string; matrix: QrMatrix }
  | { kind: 'error'; value: string }

export function QrCodeSvg({ value, size = 224, label }: QrCodeSvgProps) {
  const [state, setState] = React.useState<EncodeState>({ kind: 'loading' })

  React.useEffect(() => {
    let cancelled = false
    encodeQrMatrix(value)
      .then((matrix) => {
        if (!cancelled) setState({ kind: 'ready', value, matrix })
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error', value })
      })
    return () => {
      cancelled = true
    }
  }, [value])

  if (state.kind === 'error' && state.value === value) {
    return (
      <div
        role="alert"
        className="flex items-center justify-center rounded-md bg-st-down-bg p-3 text-center text-xs text-st-down"
        style={{ width: size, height: size }}
      >
        The pairing code could not be drawn. Generate a new code.
      </div>
    )
  }

  if (state.kind !== 'ready' || state.value !== value) {
    return <Skeleton className="rounded-md" style={{ width: size, height: size }} />
  }

  const extent = state.matrix.length + QUIET_ZONE_MODULES * 2
  // Whole pixels per module keep module edges crisp, which helps phone scanners.
  const edge = extent * Math.max(2, Math.round(size / extent))
  return (
    <svg
      role="img"
      aria-label={label}
      width={edge}
      height={edge}
      viewBox={`0 0 ${extent} ${extent}`}
      shapeRendering="crispEdges"
      className="block rounded-md ring-1 ring-border"
    >
      <rect width={extent} height={extent} fill={LIGHT_TILE} />
      <path d={qrMatrixPath(state.matrix, QUIET_ZONE_MODULES)} fill={DARK_MODULE} />
    </svg>
  )
}
